/**
 * DBCredentialsModal.tsx
 * 
 * Modal wrapper for DB Credentials in the main React app.
 * Handles:
 * - Modal UI (backdrop, close button, footer)
 * - JWT authentication via apiService
 * - Data fetching
 * 
 * Delegates all credentials UI to DBCredentialsCore.
 */

import React, { useState, useEffect } from 'react';
import { X, Database } from 'lucide-react';
import apiService from '../services/api';
import { useToast } from '../context/ToastContext';
import DBCredentialsCore from './DBCredentialsCore';
import { DBCredentials } from '../widget/types';

interface DBCredentialsModalProps {
  open: boolean;
  onClose: () => void;
  workspaceId: number;
  workspaceName?: string;
}

const DBCredentialsModal: React.FC<DBCredentialsModalProps> = ({
  open,
  onClose,
  workspaceId,
  workspaceName,
}) => {
  const { showError } = useToast();
  const [credentials, setCredentials] = useState<DBCredentials | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [closeHovered, setCloseHovered] = useState(false);

  useEffect(() => {
    if (open && workspaceId) {
      loadCredentials();
    }
  }, [open, workspaceId]);

  const loadCredentials = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.get(`/workspaces/${workspaceId}/credentials`);
      setCredentials(response.data);
    } catch (err: any) {
      const message = err.message || 'Failed to load credentials';
      setError(message);
      showError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setCredentials(null);
    setError(null);
    onClose();
  };

  if (!open) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(15, 23, 42, 0.4)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) handleClose();
      }}
    >
      <div
        style={{
          backgroundColor: 'var(--v2-surface)',
          borderRadius: 'var(--radius-lg)',
          boxShadow: 'var(--shadow-lg)',
          border: '1px solid var(--v2-border)',
          maxWidth: '540px',
          width: '90%',
          maxHeight: '90vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          fontFamily: 'var(--font-sans)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            padding: '20px 24px',
            borderBottom: '1px solid var(--v2-border)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div
              style={{
                width: '44px',
                height: '44px',
                borderRadius: 'var(--radius-md)',
                background: 'linear-gradient(135deg, var(--v2-primary) 0%, #818cf8 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 4px 6px -1px rgba(79, 70, 229, 0.3)',
              }}
            >
              <Database size={22} strokeWidth={1.5} color="#ffffff" />
            </div>
            <div>
              <h2
                style={{
                  fontSize: '1.125rem',
                  fontWeight: 700,
                  color: 'var(--v2-text)',
                  margin: 0,
                  letterSpacing: '-0.01em',
                }}
              >
                Database Credentials
              </h2>
              <p style={{ fontSize: '0.875rem', color: 'var(--v2-text-tertiary)', margin: '2px 0 0 0' }}>
                {workspaceName || 'Read-only access for external tools'}
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            style={{
              background: 'none',
              border: 'none',
              padding: '8px',
              cursor: 'pointer',
              borderRadius: 'var(--radius-sm)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'var(--v2-text-muted)',
              transition: 'all 0.15s',
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--v2-surface-hover)';
              e.currentTarget.style.color = 'var(--v2-text)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.color = 'var(--v2-text-muted)';
            }}
          >
            <X size={20} strokeWidth={1.5} />
          </button>
        </div>

        {/* Content - Delegates to Core */}
        <div style={{ padding: '24px', overflowY: 'auto', flex: 1 }}>
          <DBCredentialsCore
            credentials={credentials}
            loading={loading}
            error={error}
            mode="modal"
            onRetry={loadCredentials}
            onCopySuccess={() => {}} // Toast already handled by clipboard
            onCopyError={(msg) => showError(msg)}
          />
        </div>

        {/* Footer */}
        <div
          style={{
            padding: '16px 24px',
            borderTop: '1px solid var(--v2-border)',
            backgroundColor: 'var(--v2-surface-alt)',
            display: 'flex',
            justifyContent: 'flex-end',
          }}
        >
          <button
            onClick={handleClose}
            onMouseEnter={() => setCloseHovered(true)}
            onMouseLeave={() => setCloseHovered(false)}
            style={{
              padding: '10px 24px',
              backgroundColor: closeHovered ? 'var(--v2-primary-hover)' : 'var(--v2-primary)',
              color: '#ffffff',
              border: 'none',
              borderRadius: 'var(--radius-md)',
              fontSize: '0.875rem',
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'all 0.15s',
            }}
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

export default DBCredentialsModal;
